import type { LogQuery, LogRecord } from '@hibro/shared';
import { Emitter, LOG_BUFFER_LIMIT, matchesQuery } from '@hibro/shared';

/**
 * Page-side observability sink for the browser runtime (见 15 / 18).
 *
 * The desktop sink is `LogService` in the **main process**, deliberately above
 * the extension host so it outlives an ext-host crash. The browser has no such
 * layer: the page is the outermost thing that survives a worker restart, so the
 * sink lives here and the SharedWorker forwards its records over the port
 * (buffering the ones produced before any page connected).
 *
 * Semantics mirror `LogService` — same ring capacity, same `matchesQuery` filter,
 * same "most recent `limit`, oldest-first" result order — so the diagnostics
 * panel behaves identically on both runtimes. The ring is still per page and
 * a reload clears it. The durable copy is the SharedWorker's daily file in
 * OPFS (`hibro-logs`), not this buffer (见 15 / 18).
 */
export interface PageLogSink {
  /** Record an entry and notify subscribers. */
  append(record: LogRecord): void;
  /** Buffered records matching `query`, oldest-first. */
  query(query?: LogQuery): LogRecord[];
  /** Drop all buffered records. */
  clear(): void;
  /** Subscribe to every appended record; returns a disposer. */
  onChanged(listener: (record: LogRecord) => void): () => void;
  /**
   * The live ring, exposed for programmatic inspection (automated checks and
   * ad-hoc console scripts read it via `window.__hibroLogs`). Not for the panel,
   * which goes through {@link query}.
   */
  readonly records: LogRecord[];
}

export interface PageLogSinkOptions {
  /** Ring capacity; oldest records drop past it. Defaults to the shared 2000. */
  limit?: number;
}

export function createPageLogSink(
  options: PageLogSinkOptions = {},
): PageLogSink {
  const limit = options.limit ?? LOG_BUFFER_LIMIT;
  const records: LogRecord[] = [];
  const emitter = new Emitter<LogRecord>();

  return {
    records,
    append(record) {
      records.push(record);
      if (records.length > limit) {
        records.splice(0, records.length - limit);
      }
      emitter.fire(record);
    },
    query(query = {}) {
      const matched = records.filter((record) => matchesQuery(record, query));
      // `limit` means "the most recent N", so trim from the front and keep the
      // oldest-first order the panel renders (it reverses for display itself).
      if (query.limit !== undefined && matched.length > query.limit) {
        return matched.slice(matched.length - query.limit);
      }
      return matched;
    },
    clear() {
      records.length = 0;
    },
    onChanged: emitter.event,
  };
}
